import React from 'react';

interface SectorData {
  score?: number;
  averageScore?: number;
  totalScore?: number;
}

interface ComponentData {
  sectors?: Record<string, SectorData>;
  institutionalDebt?: SectorData | number;
  technicalDebt?: SectorData | number;
  [key: string]: SectorData | Record<string, SectorData> | number | undefined;
}

interface Dartboard360Props {
  title?: string;
  score?: number | string;
  componentData?: ComponentData | null;
  size?: number;
  isGrayed?: boolean;
  className?: string;
  showComponentBreakdown?: boolean;
}

// ── Color helpers ──────────────────────────────────────────

const RISK_COLORS: Record<string, string> = {
  low:     '#22c55e',
  medium:  '#eab308',
  high:    '#ef4444',
  default: '#2a3f5f'
};

const RISK_COLORS_DARK: Record<string, string> = {
  low:     '#166534',
  medium:  '#854d0e',
  high:    '#991b1b',
  default: '#1e3050'
};

const GRAYED_COLOR = '#3a4a6a';
const GRAYED_COLOR_DARK = '#2a3550';

function getRiskLevel(scoreVal: number): string {
  if (scoreVal >= 7) return 'high';
  if (scoreVal >= 4) return 'medium';
  if (scoreVal > 0)  return 'low';
  return 'default';
}

// ── Geometry helpers ───────────────────────────────────────

const toRad = (d: number) => d * Math.PI / 180;

function polar(cx: number, cy: number, r: number, deg: number) {
  return {
    x: cx + r * Math.cos(toRad(deg)),
    y: cy + r * Math.sin(toRad(deg))
  };
}

/**
 * True camera-aperture blade geometry.
 *
 * Each blade spans an arc on the outer circle; its inner edge spans the
 * SAME angular width but is ROTATED by `shearDeg`. This makes the two
 * straight edges angled (not radial), producing the characteristic
 * overlapping shutter-blade look when blades render in order.
 */
function generateApertureBlade(
  cx: number, cy: number,
  outerR: number, innerR: number,
  index: number, total: number,
  shearDeg: number, gapDeg: number
): string {
  const sectorDeg = 360 / total;
  const outerStart = index * sectorDeg - 90 + gapDeg / 2;
  const outerEnd   = outerStart + sectorDeg - gapDeg;
  const innerStart = outerStart + shearDeg;
  const innerEnd   = outerEnd + shearDeg;

  const pOS = polar(cx, cy, outerR, outerStart);
  const pOE = polar(cx, cy, outerR, outerEnd);
  const pIE = polar(cx, cy, innerR, innerEnd);
  const pIS = polar(cx, cy, innerR, innerStart);

  const outerLarge = (outerEnd - outerStart) > 180 ? 1 : 0;
  const innerLarge = (innerEnd - innerStart) > 180 ? 1 : 0;

  return [
    `M ${pOS.x} ${pOS.y}`,
    `A ${outerR} ${outerR} 0 ${outerLarge} 1 ${pOE.x} ${pOE.y}`,
    `L ${pIE.x} ${pIE.y}`,
    `A ${innerR} ${innerR} 0 ${innerLarge} 0 ${pIS.x} ${pIS.y}`,
    `Z`
  ].join(' ');
}

// ── Sector config ──────────────────────────────────────────

interface SectorConfig {
  keys: string[];
  labels: { text: string; position: string }[];
}

function getSectorConfig(title: string): SectorConfig {
  switch (title) {
    case 'C360':
      return {
        keys: ['governance', 'cyberDefense', 'cyberIntel', 'entSecurity'],
        labels: [
          { text: 'GOVERNANCE', position: 'top-right' },
          { text: 'CYBER\nDEFENSE', position: 'bottom-right' },
          { text: 'CYBER\nINTEL', position: 'bottom-left' },
          { text: 'ENT.\nSECURITY', position: 'top-left' }
        ]
      };
    case 'D360':
      return {
        keys: ['analysis', 'protection', 'sharing', 'collection'],
        labels: [
          { text: 'ANALYSIS', position: 'top-right' },
          { text: 'PROTECTION', position: 'bottom-right' },
          { text: 'SHARING', position: 'bottom-left' },
          { text: 'COLLECTION', position: 'top-left' }
        ]
      };
    case 'P360':
      return {
        keys: ['people', 'policy', 'tech', 'physical'],
        labels: [
          { text: 'PEOPLE', position: 'top-right' },
          { text: 'POLICY', position: 'bottom-right' },
          { text: 'TECH', position: 'bottom-left' },
          { text: 'PHYSICAL', position: 'top-left' }
        ]
      };
    case 'E360-ID':
    case 'E360-TD':
      return {
        keys: ['outsidePressure', 'people', 'governance', 'vision', 'legal'],
        labels: [
          { text: 'OUTSIDE\nPRESSURE', position: 'top' },
          { text: 'PEOPLE', position: 'top-right' },
          { text: 'GOVERNANCE', position: 'bottom-right' },
          { text: 'VISION', position: 'bottom-left' },
          { text: 'LEGAL', position: 'top-left' }
        ]
      };
    default:
      return {
        keys: ['outsidePressure', 'people', 'governance', 'vision'],
        labels: []
      };
  }
}

// ── Component ──────────────────────────────────────────────

const Dartboard360: React.FC<Dartboard360Props> = ({
  title = "360",
  score = 0,
  componentData = null,
  size = 200,
  isGrayed = true,
  className = "",
  showComponentBreakdown = false
}) => {
  const config = getSectorConfig(title);
  const sectorCount = config.keys.length;

  // Shear angle tuned per blade count for best visual match to logo
  const shearDeg = sectorCount === 5 ? 22 : 28;
  const gapDeg = 3;

  // ── Dimensions ─────────────────────────────────────────
  const center = size / 2;
  const outerRadius = size * 0.42;
  const innerRadius = size * 0.22;
  const centerCircleRadius = innerRadius - 4;

  // ── Sector color lookup ────────────────────────────────
  const getSectorColor = (sectorIndex: number): string => {
    if (isGrayed || !componentData) return GRAYED_COLOR;

    const sectorKey = config.keys[sectorIndex];
    const sectorsData = componentData.sectors;
    const sectorData = sectorsData ? sectorsData[sectorKey] : componentData[sectorKey];

    if (sectorData && typeof sectorData === 'object') {
      const sectorScore = (sectorData as SectorData).score
        || (sectorData as SectorData).averageScore || 0;
      return RISK_COLORS[getRiskLevel(sectorScore)];
    }
    return RISK_COLORS.default;
  };

  const getSectorColorDark = (sectorIndex: number): string => {
    if (isGrayed || !componentData) return GRAYED_COLOR_DARK;

    const sectorKey = config.keys[sectorIndex];
    const sectorsData = componentData.sectors;
    const sectorData = sectorsData ? sectorsData[sectorKey] : componentData[sectorKey];

    if (sectorData && typeof sectorData === 'object') {
      const sectorScore = (sectorData as SectorData).score
        || (sectorData as SectorData).averageScore || 0;
      return RISK_COLORS_DARK[getRiskLevel(sectorScore)];
    }
    return RISK_COLORS_DARK.default;
  };

  // ── Label positioning ──────────────────────────────────
  const getLabelPositionStyle = (position: string): React.CSSProperties => {
    const offset = size * 0.08;

    if (sectorCount === 5) {
      const labelRadius = size * 0.48;
      const cx = size * 0.5;
      const cy = size * 0.5;
      const angles: Record<string, number> = {
        'top': -54, 'top-right': 18, 'bottom-right': 90,
        'bottom-left': 162, 'top-left': 234
      };
      const angle = angles[position];
      if (angle !== undefined) {
        return {
          left: cx + labelRadius * Math.cos(toRad(angle)),
          top: cy + labelRadius * Math.sin(toRad(angle)),
          transform: 'translate(-50%, -50%)',
          position: 'absolute'
        };
      }
      return { position: 'absolute' };
    }

    const positions: Record<string, React.CSSProperties> = {
      'top-right':    { top: offset, right: offset, transform: 'translate(25%, -25%)', position: 'absolute' },
      'bottom-right': { bottom: offset, right: offset, transform: 'translate(25%, 25%)', position: 'absolute' },
      'bottom-left':  { bottom: offset, left: offset, transform: 'translate(-25%, 25%)', position: 'absolute' },
      'top-left':     { top: offset, left: offset, transform: 'translate(-25%, -25%)', position: 'absolute' },
      'top':          { top: offset * 0.5, left: '50%', transform: 'translate(-50%, -50%)', position: 'absolute' },
      'bottom':       { bottom: offset * 0.5, left: '50%', transform: 'translate(-50%, 50%)', position: 'absolute' },
      'right':        { top: '50%', right: offset * 0.5, transform: 'translate(50%, -50%)', position: 'absolute' },
      'left':         { top: '50%', left: offset * 0.5, transform: 'translate(-50%, -50%)', position: 'absolute' },
    };
    return positions[position] || { position: 'absolute' };
  };

  const getComponentScore = (data: SectorData | number | undefined): number => {
    if (typeof data === 'number') return data;
    if (data && typeof data === 'object') {
      return data.averageScore || data.totalScore || data.score || 0;
    }
    return 0;
  };

  // ── SVG defs ───────────────────────────────────────────
  const uid = `db-${title}-${size}`;

  return (
    <div className={`relative inline-block ${className}`}>
      <svg width={size} height={size}>
        <defs>
          <radialGradient id={`cg-${uid}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#172a45" />
            <stop offset="100%" stopColor="#0a192f" />
          </radialGradient>
          <filter id={`shadow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="rgba(0,0,0,0.5)" floodOpacity="0.5" />
          </filter>
        </defs>

        {/* Outer ring */}
        <circle
          cx={center} cy={center} r={outerRadius + 1}
          fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"
        />

        {/* Aperture blades — rendered in order for natural overlap */}
        {Array.from({ length: sectorCount }, (_, index) => {
          const color = getSectorColor(index);
          const darkColor = getSectorColorDark(index);
          const path = generateApertureBlade(
            center, center, outerRadius, innerRadius,
            index, sectorCount, shearDeg, gapDeg
          );
          return (
            <g key={index} className="transition-all duration-200">
              {/* Shadow layer */}
              <path d={path} fill={darkColor} opacity={isGrayed ? 0.2 : 0.3} transform="translate(1,1)" />
              {/* Main blade */}
              <path
                d={path}
                fill={color}
                stroke="rgba(0,0,0,0.4)"
                strokeWidth="1.5"
                opacity={isGrayed ? 0.4 : 0.92}
                filter={`url(#shadow-${uid})`}
              />
              {/* Highlight edge */}
              <path d={path} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="0.75" />
            </g>
          );
        })}

        {/* Inner ring */}
        <circle
          cx={center} cy={center} r={innerRadius}
          fill="none" stroke="rgba(0,0,0,0.2)" strokeWidth="1"
        />

        {/* Center circle */}
        <circle
          cx={center} cy={center} r={centerCircleRadius}
          fill={`url(#cg-${uid})`}
          stroke="rgba(0,217,255,0.15)"
          strokeWidth="1.5"
        />

        {/* Center text: E360 component breakdown */}
        {showComponentBreakdown && componentData && title === 'E360' ? (
          <>
            <text x={center} y={center - 12} textAnchor="middle" dominantBaseline="middle"
              className="text-xs font-bold" fill={isGrayed ? '#5a6a8a' : '#8892b0'}
            >{title}</text>
            <text x={center} y={center + 2} textAnchor="middle" dominantBaseline="middle"
              className="text-sm font-bold" fill="#3b82f6"
            >I:{getComponentScore(componentData.institutionalDebt)}</text>
            <text x={center} y={center + 12} textAnchor="middle" dominantBaseline="middle"
              className="text-sm font-bold" fill="#ef4444"
            >T:{getComponentScore(componentData.technicalDebt)}</text>
          </>
        ) : score !== '' && score !== null ? (
          <text x={center} y={center} textAnchor="middle" dominantBaseline="middle"
            className="text-2xl font-bold" fill={isGrayed ? '#5a6a8a' : '#ccd6f6'}
          >{score}</text>
        ) : null}
      </svg>

      {/* Sector labels */}
      {config.labels.map((label, index) => (
        <div
          key={index}
          className="absolute text-center"
          style={{
            ...getLabelPositionStyle(label.position),
            fontSize: `${size * 0.05}px`,
            lineHeight: 1.2
          }}
        >
          <div
            className={`font-semibold ${isGrayed ? 'text-[#5a6a8a]' : 'text-[#a8b2d1]'}`}
            style={{ whiteSpace: 'pre-line' }}
          >
            {label.text}
          </div>
        </div>
      ))}

      {/* E360 component breakdown legend */}
      {showComponentBreakdown && componentData && title === 'E360' && (
        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
          <div className="flex space-x-4 text-xs">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded mr-1" style={{ backgroundColor: '#3b82f6' }} />
              <span>Institutional</span>
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded mr-1" style={{ backgroundColor: '#ef4444' }} />
              <span>Technical</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dartboard360;
